import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";
import { generateSlugId } from "@/lib/routing/slug-id";
import { HugeiconsIcon } from "@hugeicons/react";
import { TestTube01Icon, PlusSignIcon, ArrowRight01Icon } from "@hugeicons/core-free-icons";
import { usePlanUsage } from "@/hooks/usePlanUsage";
import { useSubscription } from "@/lib/contexts/SubscriptionContext";

interface SiteData {
  propertyId: string;
  name: string;
  domain: string;
  views: number;
  events: number;
  workspaceId?: string;
  workspaceName?: string;
}

interface UsageChartProps {
  sites: SiteData[];
}

const SPARKLINE_COLORS = [
  "var(--chart-1)",
  "var(--chart-2)",
  "var(--chart-3)",
  "var(--chart-4)",
  "var(--chart-5)",
  "var(--primary)",
];

/** Solid hex colors for avatars - avoids CSS variable gradient issues */
const AVATAR_COLORS = [
  "#EA580C",
  "#2563eb",
  "#059669",
  "#7c3aed",
  "#dc2626",
  "#0891b2",
];

function SiteGradientAvatar({
  domain,
  color,
  size = 20,
}: {
  domain: string;
  color: string;
  size?: number;
}) {
  const initials = domain
    .replace(/^www\./, "")
    .split(".")[0]
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      className="rounded-md flex items-center justify-center shrink-0 text-white font-semibold shadow-sm"
      style={{
        width: size,
        height: size,
        background: `linear-gradient(135deg, ${color}, ${color}cc)`,
        fontSize: Math.max(9, size * 0.4),
      }}
    >
      {initials}
    </div>
  );
}

const formatCompact = (n: number) =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`
    : n >= 1000
      ? `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`
      : n.toString();

function generateSparklinePoints(seed: number, count: number = 12): number[] {
  const points: number[] = [];
  let value = 0.3 + (seed % 7) * 0.08;
  for (let i = 0; i < count; i++) {
    const noise =
      Math.sin(seed * 13.37 + i * 2.1) * 0.15 +
      Math.cos(seed * 7.13 + i * 1.7) * 0.1;
    value = Math.max(0.05, Math.min(0.95, value + noise));
    if (i > count * 0.6) value = Math.min(0.95, value + 0.03);
    points.push(value);
  }
  return points;
}

export function MiniSparkline({
  points,
  color,
  width = 80,
  height = 32,
}: {
  points: number[];
  color: string;
  width?: number;
  height?: number;
}) {
  const padding = 2;
  const usableW = width - padding * 2;
  const usableH = height - padding * 2;

  const pathPoints = points.map((p, i) => ({
    x: padding + (i / (points.length - 1)) * usableW,
    y: padding + (1 - p) * usableH,
  }));

  let d = `M ${pathPoints[0].x} ${pathPoints[0].y}`;
  for (let i = 1; i < pathPoints.length; i++) {
    const prev = pathPoints[i - 1];
    const curr = pathPoints[i];
    const cpx = (prev.x + curr.x) / 2;
    d += ` C ${cpx} ${prev.y}, ${cpx} ${curr.y}, ${curr.x} ${curr.y}`;
  }

  const lastPoint = pathPoints[pathPoints.length - 1];
  const areaD = `${d} L ${lastPoint.x} ${height} L ${pathPoints[0].x} ${height} Z`;

  const gradientId = `sparkGrad-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <svg width={width} height={height} className="overflow-visible">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.35} />
          <stop offset="100%" stopColor={color} stopOpacity={0.08} />
        </linearGradient>
      </defs>
      <path d={areaD} fill={`url(#${gradientId})`} />
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastPoint.x} cy={lastPoint.y} r={2} fill={color} />
    </svg>
  );
}

export {
  generateSparklinePoints,
  SPARKLINE_COLORS,
  AVATAR_COLORS,
  SiteGradientAvatar,
};

export default function UsageChart({ sites }: UsageChartProps) {
  const navigate = useNavigate();
  const { openPricingModal } = useSubscription();
  // Websites are account-wide (scope: 'account'), so any property under the
  // same subscription owner resolves the same limit.
  const { websites: websitesUsage } = usePlanUsage({ propertyId: sites[0]?.propertyId });

  const topSites = useMemo(() => {
    return sites
      .filter((s) => s.propertyId)
      .sort((a, b) => b.views - a.views)
      .slice(0, 6)
        .map((s, i) => ({
        propertyId: s.propertyId,
        name: s.name,
        domain: s.domain,
        views: s.views,
        events: s.events,
        workspaceId: s.workspaceId,
        workspaceName: s.workspaceName,
        sparkline: generateSparklinePoints(
          s.domain.split("").reduce((a, c) => a + c.charCodeAt(0), 0) + i
        ),
        color: SPARKLINE_COLORS[i % SPARKLINE_COLORS.length],
        avatarColor: AVATAR_COLORS[i % AVATAR_COLORS.length],
      }));
  }, [sites]);

  const totalViews = sites.reduce((sum, s) => sum + s.views, 0);

  const handleNavigateToSite = (site: {
    propertyId: string;
    name: string;
    domain: string;
    workspaceId?: string;
    workspaceName?: string;
  }) => {
    if (site.workspaceId && site.workspaceName) {
      const workspaceSlugId = generateSlugId(site.workspaceName, site.workspaceId);
      navigate(`/workspace/${workspaceSlugId}/traffic?site=${site.propertyId}`);
    } else {
      const slugId = generateSlugId(site.name || site.domain, site.propertyId);
      navigate(`/p/${slugId}/traffic`);
    }
  };

  const handleNewSplitTest = (
    e: React.MouseEvent,
    site: {
      propertyId: string;
      name: string;
      domain: string;
      workspaceId?: string;
      workspaceName?: string;
    }
  ) => {
    e.stopPropagation();
    if (site.workspaceId && site.workspaceName) {
      const workspaceSlugId = generateSlugId(site.workspaceName, site.workspaceId);
      navigate(`/workspace/${workspaceSlugId}/split-testing/new?site=${site.propertyId}`);
    } else {
      const slugId = generateSlugId(site.name || site.domain, site.propertyId);
      navigate(`/p/${slugId}/split-testing/new`);
    }
  };

  if (topSites.length === 0) {
    return (
      <Card className="col-span-full flex items-center justify-center">
        <CardContent className="text-center py-12">
          <p className="text-sm text-muted-foreground">
            No usage data yet. Add a site to get started.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Top sites</h3>
        <span className="text-xs text-muted-foreground tabular-nums">
          {formatCompact(totalViews)} total views
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {topSites.map((site, index) => (
          <motion.div
            key={site.domain}
            className="flex flex-col"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.3,
              delay: index * 0.05,
              ease: "easeOut",
            }}
          >
            <Card
              className="group hover:shadow-md hover:border-border/60 transition-all duration-200 cursor-pointer relative"
              onClick={() => handleNavigateToSite(site)}
            >
              <CardContent className="p-3 flex flex-col justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <SiteGradientAvatar
                    domain={site.domain}
                    color={site.avatarColor}
                    size={20}
                  />
                  <span className="text-xs font-medium text-foreground truncate leading-tight group-hover:text-primary transition-colors">
                    {site.domain}
                  </span>
                </div>
                <div className="flex items-end justify-between gap-1">
                  <div>
                    <div className="text-base font-semibold text-foreground tabular-nums leading-none">
                      {formatCompact(site.views)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      views
                    </div>
                  </div>
                  <MiniSparkline
                    points={site.sparkline}
                    color={site.color}
                    width={72}
                    height={28}
                  />
                </div>
                {site.events > 0 && (
                  <div className="text-xs text-muted-foreground/70 tabular-nums border-t border-border/20 pt-1.5 -mb-0.5 flex items-center justify-between">
                    <span>{formatCompact(site.events)} events</span>
                    <span className="flex items-center gap-0.5 text-muted-foreground/60 group-hover:text-primary transition-colors text-xs font-medium">
                      View analytics
                    </span>
                  </div>
                )}
                {!(site.events > 0) && (
                  <div className="flex items-center justify-end -mb-0.5">
                    <span className="text-muted-foreground/60 group-hover:text-primary transition-colors text-xs font-medium">
                      View analytics
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
            <button
              onClick={(e) => handleNewSplitTest(e, site)}
              className="mt-1.5 w-full flex items-center justify-center gap-1.5 rounded-md border border-border/30 bg-card px-2 py-1.5 text-xs font-medium text-muted-foreground hover:border-primary/40 hover:text-primary hover:bg-primary/5 transition-colors"
            >
              <HugeiconsIcon icon={TestTube01Icon} size={11} />
              New split test
            </button>
          </motion.div>
        ))}

        {/* Add site card — fills the next empty slot up to 6 */}
        {topSites.length < 6 && (
          <motion.div
            className="flex flex-col"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: topSites.length * 0.05, ease: "easeOut" }}
          >
            <Card
              className="group hover:shadow-md hover:border-primary/40 transition-all duration-200 cursor-pointer border-dashed border-border/50"
              onClick={() => (websitesUsage.canAdd ? navigate("/add-website") : openPricingModal())}
            >
              <CardContent className="p-3 flex flex-col justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-5 h-5 rounded-md flex items-center justify-center shrink-0 bg-muted/60 group-hover:bg-primary/10 transition-colors">
                    <HugeiconsIcon
                      icon={websitesUsage.canAdd ? PlusSignIcon : ArrowRight01Icon}
                      size={11}
                      className="text-muted-foreground group-hover:text-primary transition-colors"
                    />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground group-hover:text-primary transition-colors truncate leading-tight">
                    {websitesUsage.canAdd ? "Add site" : "Upgrade"}
                  </span>
                </div>
                <div className="flex items-end justify-between gap-1">
                  <div>
                    <div className="text-base font-semibold text-muted-foreground/30 tabular-nums leading-none">—</div>
                    <div className="text-xs text-muted-foreground/50 mt-0.5">views</div>
                  </div>
                </div>
                <div className="flex items-center justify-end -mb-0.5">
                  <span className="text-muted-foreground/40 group-hover:text-primary transition-colors text-xs font-medium">
                    Connect →
                  </span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
